import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export function DeleteEmployeeDialog({ open, onClose, session, employee, onDeleted }) {
  const [prev, setPrev] = useState(null);
  const [mode, setMode] = useState("ARCHIVE");
  const [reason, setReason] = useState("");
  const [lwd, setLwd] = useState("");
  const [typed, setTyped] = useState("");
  const [erroneous, setErroneous] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !employee?.employee_id) return;
    setPrev(null); setReason(""); setTyped(""); setErroneous(false); setMode("ARCHIVE");
    api.post("/employees/delete-preview", { token: session.token, employee_id: employee.employee_id })
      .then((r) => { setPrev(r.data); setLwd(r.data.dependencies.attendance_last || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load dependencies"));
  }, [open, employee, session.token]);

  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post("/employees/delete", { token: session.token, employee_id: employee.employee_id,
        mode, reason, confirm_text: typed, last_working_date: mode === "ARCHIVE" ? lwd || null : null,
        delete_erroneous_attendance: erroneous });
      toast.success(`Employee ${r.data.status === "ARCHIVED" ? "archived and removed from active modules" : "permanently deleted"}`);
      onDeleted(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } finally { setBusy(false); }
  };

  const d = prev?.dependencies;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="delete-employee-dialog">
        <DialogHeader>
          <DialogTitle>Delete employee · {employee?.name}</DialogTitle>
          <DialogDescription>Employee ID {employee?.employee_id}. Finalised payroll and payment evidence are never deleted.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-3 text-sm">
            <div className="rounded border p-2 text-xs space-y-0.5" data-testid="delete-employee-dependencies">
              <div>Attendance rows: <b>{d.attendance_rows}</b> {d.attendance_first && `(${d.attendance_first} → ${d.attendance_last}, ${d.attendance_centres.join(", ")})`}</div>
              <div>Finalised payroll months: <b>{d.finalised_payroll_months.join(", ") || "none"}</b></div>
              <div>Historical payroll PDFs: {d.historical_payroll_snapshots} · Advances: {d.advances} · Salary slips: {d.salary_slips} · Payments: {d.payments}</div>
              <div>Open transfers (will be cancelled): {d.open_transfers}</div>
            </div>
            <div className="flex gap-2">
              {prev.modes.map((m) => (
                <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} onClick={() => setMode(m)}
                  data-testid={`delete-mode-${m}`}>{m === "ARCHIVE" ? "Archive (has history)" : "Permanent (entered by mistake)"}</Button>))}
            </div>
            {!d.permanent_allowed && <p className="text-xs text-amber-700">Permanent delete blocked by: {d.permanent_blockers.join(", ")}.</p>}
            {mode === "ARCHIVE" && (
              <div><label className="text-xs">Last working date</label>
                <Input type="date" value={lwd} onChange={(e) => setLwd(e.target.value)} data-testid="delete-lwd" />
                <p className="text-[11px] text-muted-foreground">Attendance after this date is moved to the archive. Earlier payable days stay payable.</p></div>)}
            {mode === "PERMANENT" && d.attendance_rows > 0 && (
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={erroneous}
                onChange={(e) => setErroneous(e.target.checked)} data-testid="delete-erroneous-attendance" />
                The {d.attendance_rows} attendance rows were entered in error — remove them too.</label>)}
            <Textarea rows={2} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="delete-reason" />
            <div><label className="text-xs">Type the Employee ID <b>{employee?.employee_id}</b> to confirm</label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-id" /></div>
          </div>)}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={go} data-testid="delete-employee-confirm"
            disabled={busy || !d || typed.trim() !== String(employee?.employee_id) || reason.trim().length < 5}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Delete employee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
